import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import {
  getToolMapActionLabelDictionary,
  getToolMapLabelDictionary,
  getWidgetModalLabelDictionary,
} from 'app/shared/constants/widget.constant';
import { ToolMapActionType } from 'app/shared/models/tool-action-type';
import { ToolMapType } from 'app/shared/models/tool-map-type';
import { WidgetModalType } from 'app/shared/models/widget-modal-type';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { ArrayUtils } from 'app/shared/utils/array.utils';
import { MainMapService } from '../../main-map.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from 'app/shared/services/translation.service';

interface MapToolIemLike {
  icon: string;
  tooltip?: string;
  selected?: boolean;
  disabled?: boolean;
  shouldRemove?: boolean;
  visible?: Observable<boolean>;
  highlightMode?: boolean;
  onClick?: () => void;
  link?: string;
}

@Component({
  selector: 'app-map-tool-list',
  templateUrl: 'map-tool-list.component.html',
  styleUrls: ['map-tool-list.component.scss'],
})
export class MapToolListComponent implements OnInit, OnDestroy {
  items: MapToolIemLike[] = [];

  constructor(
    private mainMapService: MainMapService,
    private appConfig: AppConfigService,
    private screenService: ScreenService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    const thisRef = new WeakRef(this);

    const { siiaMode } = this.appConfig;

    const observeVisibleFn = (
      visible?: boolean | Observable<boolean>
    ): Observable<boolean> => {
      if (!(visible instanceof Observable)) {
        visible = of(visible ?? true);
      }

      return visible;
    };

    const createItemRelativeWidgetModal = (
      type: WidgetModalType,
      cfg?: {
        icon?: string;
        shouldRemove?: boolean;
        visible?: boolean | Observable<boolean>;
        highlightMode?: boolean;
      }
    ): MapToolIemLike => {
      const text = getWidgetModalLabelDictionary(this.translationService).get(type);

      const visible = observeVisibleFn(cfg?.visible);

      return {
        shouldRemove: !!cfg?.shouldRemove,
        visible,
        highlightMode: !!cfg?.highlightMode,
        tooltip: text,
        icon: cfg?.icon,
        get selected() {
          return thisRef.deref().mainMapService.isWidgetModalActive(type);
        },
        onClick: () => {
          thisRef.deref().mainMapService.toggleWidgetModal(type);
        },
      };
    };

    const createItemRelativeTool = (
      type: ToolMapType,
      cfg?: {
        icon?: string;
        shouldRemove?: boolean;
        visible?: boolean | Observable<boolean>;
        highlightMode?: boolean;
      }
    ): MapToolIemLike => {
      const text = getToolMapLabelDictionary(this.translationService).get(type);

      const visible = observeVisibleFn(cfg?.visible);

      return {
        shouldRemove: !!cfg?.shouldRemove,
        visible,
        highlightMode: !!cfg?.highlightMode,
        tooltip: text,
        icon: cfg?.icon,
        get selected() {
          return thisRef.deref().mainMapService.isToolMapActive(type);
        },
        onClick: () => {
          thisRef.deref().mainMapService.toggleToolMap(type);
        },
      };
    };

    const createItemRelativeToolAction = (
      type: ToolMapActionType,
      cfg?: {
        icon?: string;
        shouldRemove?: boolean;
        visible?: boolean | Observable<boolean>;
        highlightMode?: boolean;
      }
    ): MapToolIemLike => {
      const text = getToolMapActionLabelDictionary(this.translationService).get(type);

      const visible = observeVisibleFn(cfg?.visible);

      return {
        shouldRemove: !!cfg?.shouldRemove,
        visible,
        highlightMode: !!cfg?.highlightMode,
        tooltip: text,
        icon: cfg?.icon,
        get disabled() {
          return !thisRef.deref().mainMapService.canRequestToolMapAction(type);
        },
        onClick: () => {
          thisRef.deref().mainMapService.requestToolMapAction(type);
        },
      };
    };

    const invisibleWhenMobileObservable = this.screenService
      .observeIsMobile()
      .pipe(map((x) => !x));

    this.items = [
      createItemRelativeWidgetModal(WidgetModalType.AdvancedSearch, {
        icon: 'assets/icons/search.svg',
        shouldRemove: !siiaMode,
      }),
      createItemRelativeWidgetModal(WidgetModalType.MapManagement, {
        icon: 'assets/icons/bookmark.svg',
        shouldRemove: !siiaMode,
      }),
      createItemRelativeTool(ToolMapType.CurrentLocation, {
        icon: 'assets/icons/current-location.svg',
      }),
      createItemRelativeToolAction(ToolMapActionType.Clear, {
        icon: 'assets/icons/eraser.svg',
        visible: siiaMode ? true : invisibleWhenMobileObservable,
      }),
      createItemRelativeWidgetModal(WidgetModalType.Help, {
        icon: 'assets/icons/help-outline.svg',
        shouldRemove: siiaMode,
      }),
      createItemRelativeTool(ToolMapType.AvailableCoverage, {
        icon: 'assets/icons/house-wireless.svg',
        shouldRemove: siiaMode,
        highlightMode: true,
      }),
      createItemRelativeTool(ToolMapType.ZoomByWindow, {
        icon: 'assets/icons/identify.svg',
        visible: invisibleWhenMobileObservable,
        shouldRemove: siiaMode,
      }),
      createItemRelativeTool(ToolMapType.StreetView, {
        icon: 'assets/icons/street-view.svg',
      }),
      createItemRelativeTool(ToolMapType.Identify, {
        icon: 'assets/icons/information-outline.svg',
      }),
    ];

    ArrayUtils.removeItemsBy(this.items, (x) => x.shouldRemove);
  }

  ngOnDestroy() {
    this.items = null;
  }
}
